import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flag, Check, ExternalLink } from "lucide-react";

export default function AdminReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");

  useEffect(() => {
    if (user === false) navigate("/login", { state: { from: "/admin/reports" } });
    else if (user && user.role !== "admin") navigate("/", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports");
      setReports(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  const resolve = async (r) => {
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      toast("Marked resolved.");
      await load();
    } catch (e) {
      toast("Couldn't resolve", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  if (user && user.role !== "admin") return null;
  const filtered = reports.filter((r) => tab === "open" ? r.status === "open" : true);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="admin-reports-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Editor desk</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Reports</h1>
      <p className="text-[#786B63] mt-3 max-w-xl">
        Community-flagged cafés and reviews. Look, judge, resolve.
      </p>

      <div className="mt-8 border-b border-border flex gap-8">
        <button
          onClick={() => setTab("open")}
          data-testid="reports-tab-open"
          className={`pb-3 -mb-px text-sm tracking-wide uppercase ${tab === "open" ? "text-[#C05A46] border-b-2 border-[#C05A46]" : "text-[#786B63]"}`}
        >
          Open · {reports.filter((r) => r.status === "open").length}
        </button>
        <button
          onClick={() => setTab("all")}
          data-testid="reports-tab-all"
          className={`pb-3 -mb-px text-sm tracking-wide uppercase ${tab === "all" ? "text-[#C05A46] border-b-2 border-[#C05A46]" : "text-[#786B63]"}`}
        >
          All · {reports.length}
        </button>
      </div>

      <div className="mt-8 space-y-4">
        {loading ? (
          [...Array(2)].map((_, i) => <div key={i} className="border border-border bg-card rounded-sm h-28 animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="border border-border bg-card rounded-sm p-10 text-center">
            <Flag className="h-6 w-6 text-[#C05A46] mx-auto" strokeWidth={1.5} />
            <p className="font-serif text-2xl mt-3">Nothing flagged.</p>
            <p className="text-sm text-[#786B63] mt-1">Community is happy.</p>
          </div>
        ) : filtered.map((r) => (
          <div key={r.id} data-testid={`report-${r.id}`} className="border border-border bg-card rounded-sm p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-[#786B63]">
                  {r.target_type === "cafe" ? "Café reported" : "Review reported"} · {new Date(r.created_at).toLocaleDateString()} · by {r.reporter_name}
                </p>
                <h3 className="font-serif text-2xl mt-1">
                  {r.target_type === "cafe"
                    ? r.target_preview?.name || "(deleted)"
                    : `"${(r.target_preview?.comment || "(deleted)").slice(0, 80)}…"`}
                </h3>
                <p className="text-sm text-[#2C1E16]/90 mt-2 leading-relaxed">
                  <span className="text-[#786B63]">Reason:</span> {r.reason}
                </p>
                {r.target_type === "cafe" && r.target_preview?.id && (
                  <Link
                    to={`/cafe/${r.target_preview.id}`}
                    className="text-xs text-[#C05A46] inline-flex items-center gap-1 mt-3"
                  >
                    <ExternalLink size={12} strokeWidth={1.5} /> View café
                  </Link>
                )}
                {r.target_type === "review" && r.target_preview?.cafe_id && (
                  <Link
                    to={`/cafe/${r.target_preview.cafe_id}`}
                    className="text-xs text-[#C05A46] inline-flex items-center gap-1 mt-3"
                  >
                    <ExternalLink size={12} strokeWidth={1.5} /> View café (reviewed by {r.target_preview?.user_name})
                  </Link>
                )}
              </div>
              {r.status === "open" ? (
                <Button
                  size="sm"
                  onClick={() => resolve(r)}
                  className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]"
                  data-testid={`resolve-${r.id}`}
                >
                  <Check className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Mark resolved
                </Button>
              ) : (
                <span className="text-xs uppercase tracking-wider px-2 py-1 border border-border rounded-full text-[#786B63]">
                  Resolved
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
